import { TimeZone } from "@/formatting/datetime";
import { type EventOverview } from "@/schemas/events/EventOverview";
import { EventTag } from "@/schemas/events/EventTag";
import { EventCategory } from "@/schemas/events/filter/attributes/EventCategorySet";
import { EventStatus } from "@/schemas/events/filter/attributes/EventStatusSet";

export const PRIDE_EVENTS_2025: EventOverview[] = [
  {
    id: "1",
    title: "Taipei Pride Parade",
    description: `
      Taipei Pride stands as Asia's largest LGBTQ+ celebration, drawing participants from across the continent and around the world. As Taiwan continues to lead the way in LGBTQ+ rights in Asia, being the first country in the region to legalize same-sex marriage in 2019, its annual Pride celebration has become a beacon of hope and progress for the entire region.
    
      Taipei Pride began in 2003 with just a few hundred participants, growing exponentially to become one of Asia's most significant human rights events. The celebration has played a crucial role in advancing LGBTQ+ rights in Taiwan, contributing to milestone achievements like marriage equality and anti-discrimination protections
    
      The 2025 celebration is expected to continue Taipei Prides tradition of combining festive celebration with social activism. The event typically takes place in late October, making it one of the few major Pride celebrations held outside the traditional summer season. This timing allows for more comfortable weather conditions and distinguishes it from other global Pride events.`,
    type: EventCategory.PARADE,
    status: EventStatus.CONFIRMED,
    coverImageUri: require("@/assets/images/screens/events/taipei/cover.jpg"),
    images: [],
    startDateTime: "2025-10-25T12:00:00+08:00",
    endDateTime: "2025-10-25T18:30:00+08:00",
    timeZone: TimeZone.ASIA_TAIPEI,
    location: {
      coordinates: {
        latitude: 25.0375, // Latitude for Taipei City Hall Plaza
        longitude: 121.5645, // Longitude for Taipei City Hall Plaza
      },
      address: {
        country: "Taiwan",
        countryCode: "TW",
        street: "No. 1, Shifu Rd, Xinyi District",
        city: "Taipei",
        state: null,
        stateCode: null,
        postalCode: "110",
        county: null,
      },
    },
    schedule: [
      {
        startDateTime: "12:00",
        endDateTime: "12:45",
        title: "Rainbow Market Begins",
        description: "",
      },
      {
        startDateTime: "12:45",
        endDateTime: "13:00",
        title: "Opening Ceremony",
        description: "Featured Artists & Community Speakers",
      },
      {
        startDateTime: "13:00",
        endDateTime: "14:00",
        title: "Assembly",
        description: "Route: City Hall → Ketagalan Boulevard",
      },
      {
        startDateTime: "14:00",
        endDateTime: "16:00",
        title: "Parade Departure",
        description: "Celebration Concert & Community Awards",
      },
      {
        startDateTime: "16:00",
        endDateTime: "18:00",
        title: "Second-Half of Stage Show Begins",
        description: "",
      },
      {
        startDateTime: "18:00",
        endDateTime: "18:30",
        title: "Closing Ceremony",
        description: "",
      },
    ],
    highlights: [],
    gallery: {
      id: "opening-ceremony",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/taipei/gallery/people/elvis_ian.jpg"),
          alt: "Elvis Ian performing at Taipei Pride opening ceremony",
          tags: ["people"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/taipei/gallery/people/group.jpeg"),
          alt: "Group photo of Taipei Pride parade participants with rainbow flags",
          tags: ["people"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/taipei/gallery/people/jiangmengru.jpg"),
          alt: "Jiang Mengru speaking at Taipei Pride event",
          tags: ["people"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/taipei/gallery/people/kawinn.jpg"),
          alt: "Kawinn performing at Taipei Pride main stage",
          tags: ["people"],
        },
        {
          id: "5",
          uri: require("@/assets/images/screens/events/taipei/gallery/route/map.png"),
          alt: "Taipei Pride parade route map showing path from City Hall to Ketagalan Boulevard",
          tags: ["map"],
        },
        {
          id: "6",
          uri: require("@/assets/images/screens/events/taipei/gallery/route/venue.png"),
          alt: "Aerial view of Taipei Pride venue layout at City Hall Plaza",
          tags: ["map"],
        },
        {
          id: "7",
          uri: require("@/assets/images/screens/events/taipei/gallery/swag/bag.jpg"),
          alt: "Official Taipei Pride 2025 tote bag with rainbow design",
          tags: ["swag"],
        },
        {
          id: "8",
          uri: require("@/assets/images/screens/events/taipei/gallery/swag/shirt.jpg"),
          alt: "Limited edition Taipei Pride 2025 t-shirt featuring event logo",
          tags: ["swag"],
        },
        {
          id: "9",
          uri: require("@/assets/images/screens/events/taipei/gallery/swag/stickers.jpg"),
          alt: "Collection of colorful Taipei Pride stickers and pins",
          tags: ["swag"],
        },
      ],
      tags: [
        { id: "people", label: "people", color: "#FF0000" },
        { id: "map", label: "map", color: "#00FF00" },
        { id: "swag", label: "swag", color: "#FFFF00" },
      ],
    },
    expectedAttendance: 100000,
    tags: [EventTag.OFFICIAL, EventTag.FEATURE],
    website: "https://www.taiwanpride.lgbt/",
    links: [
      {
        type: "facebook",
        url: "https://www.facebook.com/Taiwan.LGBT.Pride/",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/taiwanlgbtpride/",
      },
      {
        type: "youtube",
        url: "https://www.youtube.com/channel/UCtoAZmEzpLysmE4UyrJWHPA/featured",
      },
      {
        type: "website",
        url: "https://www.taiwanpride.lgbt/",
      },
    ],
    transportations: [
      {
        mode: "train",
        title: "MRT",
        duration: "5 mins walk from station",
        icon: "train",
        instructions: [
          "Take Blue Line to Taipei City Hall Station",
          "Use Exit 2",
          "Walk straight for 400m",
          "Event venue will be on your right",
        ],
      },
      {
        mode: "rideshare",
        title: "Uber/Taxi",
        duration: "15-20 mins",
        icon: "local-taxi",
        instructions: [
          'Set destination as "Taipei City Hall Plaza"',
          "Drop-off point at main entrance",
          "Follow signs to event area",
        ],
      },
      {
        mode: "bus",
        title: "Bus",
        duration: "10 mins total",
        icon: "directions-bus",
        instructions: [
          "Take routes 202, 212, 266, or 281",
          "Get off at City Hall Stop",
          "Cross at the pedestrian crossing",
          "Event venue is directly ahead",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "15 mins from downtown",
        icon: "directions-walk",
        instructions: [
          "Start from Xinyi Shopping District",
          "Head east on Xinyi Road",
          "Turn right at City Hall Road",
          "Continue for 300m to venue",
        ],
      },
    ],
  },
  {
    id: "9",
    title: "Tokyo Pride Parade 2025",
    description: `
      Tokyo Pride, known in Japanese as Tokyo Rainbow Pride (東京レインボープライド), represents one of Japan's most significant LGBTQ+ celebrations. As the country's largest Pride event, it serves as a crucial platform for visibility, advocacy, and celebration in a society where LGBTQ+ rights continue to evolve.

      Tokyo's Pride journey began in 1994 with the Tokyo Lesbian Gay Parade, though the modern iteration of Tokyo Rainbow Pride as we know it today started in 2012. The event has grown from a small gathering to a major celebration attracting tens of thousands of participants, reflecting changing attitudes toward LGBTQ+ rights in Japan.

    `,
    type: EventCategory.PARADE,
    status: EventStatus.CONFIRMED,
    coverImageUri: require("@/assets/images/screens/events/tokyo/cover.jpg"),
    images: [],
    startDateTime: "2025-06-07T10:00:00+09:00",
    endDateTime: "2025-06-08T18:00:00+09:00",
    timeZone: TimeZone.ASIA_TOKYO,
    location: {
      coordinates: {
        latitude: 35.6717, // Latitude for Yoyogi Park
        longitude: 139.6949, // Longitude for Yoyogi Park
      },
      address: {
        country: "Japan",
        countryCode: "JP",
        street: "Yoyogi Park",
        city: "Tokyo",
        state: null,
        stateCode: "13",
        postalCode: "150-0001",
        county: null,
      },
    },
    schedule: [
      {
        startDateTime: "10:00",
        endDateTime: "11:00",
        title: "Pride Festival Area Opens",
        description: "Yoyogi Park gates open, booth setup begins",
      },
      {
        startDateTime: "11:00",
        endDateTime: "11:45",
        title: "Welcome Reception",
        description:
          "Registration for parade participants begins at Yoyogi Park",
      },
      {
        startDateTime: "11:45",
        endDateTime: "12:30",
        title: "Opening Ceremony",
        description:
          "Traditional Japanese drum performance, speeches from organizers and special guests",
      },
      {
        startDateTime: "12:30",
        endDateTime: "13:30",
        title: "Pride Stage Performances Begin",
        description:
          "J-Pop artists, drag performances, and local LGBTQ+ performers",
      },
      {
        startDateTime: "13:30",
        endDateTime: "14:00",
        title: "Parade Assembly",
        description:
          "Participants gather at starting point near Harajuku Station",
      },
      {
        startDateTime: "14:00",
        endDateTime: "16:30",
        title: "Main Pride Parade",
        description:
          "Route: Harajuku → Shibuya → Yoyogi Park. Features community groups, corporate allies, and performance units",
      },
      {
        startDateTime: "16:30",
        endDateTime: "17:30",
        title: "Pride Festival Peak Time",
        description:
          "All booths open, main stage performances, food court activities at maximum capacity",
      },
      {
        startDateTime: "17:30",
        endDateTime: "18:30",
        title: "Rainbow Connection Hour",
        description:
          "Community networking event, LGBTQ+ organization presentations, and activist speeches",
      },
      {
        startDateTime: "18:30",
        endDateTime: "19:00",
        title: "Sunset Ceremony",
        description: "Candlelight moment, community awards presentation",
      },
      {
        startDateTime: "19:00",
        endDateTime: "20:00",
        title: "Closing Celebration",
        description:
          "Final performances, farewell address, and announcement of next year's theme",
      },
    ],
    highlights: [],
    gallery: {
      id: "tokyo-pride",
      title: "Tokyo Pride 2025",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/tokyo/gallery/niroren.jpg"),
          alt: "Japanese drum performers Niroren leading the opening ceremony at Tokyo Pride",
          tags: ["people"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/tokyo/gallery/photo1.jpg"),
          alt: "Diverse crowd gathering at Yoyogi Park entrance with rainbow flags for Tokyo Pride",
          tags: ["people"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/tokyo/gallery/photo2.jpg"),
          alt: "LGBTQ+ community members and allies marching through Harajuku during Tokyo Pride parade",
          tags: ["people"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/tokyo/gallery/photo3.jpg"),
          alt: "Performance stage at Yoyogi Park featuring local artists and drag performers at Tokyo Pride",
          tags: ["people"],
        },
      ],
      tags: [{ id: "people", label: "people", color: "#FF0000" }],
    },
    expectedAttendance: 50000,
    tags: [EventTag.FEATURE, EventTag.OFFICIAL],
    website: "https://pride.tokyo",
    links: [
      {
        type: "facebook",
        url: "https://www.facebook.com/Tokyo.R.Pride/",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/tokyo.r.pride/",
      },
      {
        type: "youtube",
        url: "https://www.youtube.com/c/TokyoRPride",
      },
      {
        type: "website",
        url: "https://pride.tokyo/",
      },
    ],
    transportations: [
      {
        mode: "train",
        title: "JR Line",
        duration: "3 mins walk from station",
        icon: "train",
        instructions: [
          "Take JR Yamanote Line to Harajuku Station",
          "Use Takeshita Exit",
          "Walk towards Yoyogi Park",
          "Pride festival area will be visible on your left",
        ],
      },
      {
        mode: "train",
        title: "Tokyo Metro",
        duration: "7 mins walk from station",
        icon: "subway",
        instructions: [
          "Take Chiyoda Line to Meiji-Jingumae Station",
          "Use Exit C3",
          "Follow signs to Yoyogi Park",
          "Pride event entrance will be ahead",
        ],
      },
      {
        mode: "rideshare",
        title: "Taxi/Uber",
        duration: "20-30 mins from Tokyo Station",
        icon: "local-taxi",
        instructions: [
          'Set destination as "Yoyogi Park Event Space" (代々木公園イベント広場)',
          "Request drop-off at main entrance",
          "Show driver: 代々木公園のプライドイベント会場まで",
          "Look for Pride flags and follow crowd",
        ],
      },
      {
        mode: "bus",
        title: "City Bus",
        duration: "5 mins walk from stop",
        icon: "directions-bus",
        instructions: [
          "Take Toei Bus 81 or 86",
          "Get off at Yoyogi Park Mae stop",
          "Cross at the marked crossing",
          "Follow signs to festival grounds",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "15 mins from Shibuya",
        icon: "directions-walk",
        instructions: [
          "Start from Shibuya Crossing",
          "Head north on Meiji Dori",
          "Turn left at Yoyogi Park entrance",
          "Follow Pride event signage",
        ],
      },
    ],
  },
  {
    id: "2",
    title: "Bangkok Pride Parade",
    description: `
      Bangkok Pride represents a vibrant celebration of Thailand's LGBTQ+ community, reflecting the country's relatively accepting attitude toward gender and sexual diversity. As one of Southeast Asia's most prominent Pride events, it showcases Thailand's unique blend of traditional culture and modern LGBTQ+ expression.

      While Thailand has long been known for its relative acceptance of LGBTQ+ individuals, Bangkok Pride has had an intermittent history. After a hiatus, the event was revived with renewed energy, demonstrating the community's resilience and growing advocacy for LGBTQ+ rights in Thailand.

    `,
    type: EventCategory.PARADE,
    status: EventStatus.CONFIRMED,
    coverImageUri: require("@/assets/images/screens/events/bangkok/cover.jpg"),
    images: [],
    startDateTime: "2025-06-01T14:00:00+07:00",
    endDateTime: "2025-06-01T20:00:00+07:00",
    timeZone: TimeZone.ASIA_BANGKOK,
    location: {
      coordinates: {
        latitude: 13.7461, // Approximate latitude for National Stadium Supachalasai
        longitude: 100.5306, // Approximate longitude for National Stadium Supachalasai
      },
      address: {
        country: "Thailand",
        countryCode: "TH",
        street: "Rama I Road",
        city: "Bangkok",
        state: null,
        stateCode: null,
        postalCode: null,
        county: null,
      },
    },
    schedule: [
      {
        startDateTime: "16:00",
        endDateTime: "17:00",
        title: "Pride Village Opens",
        description: "Event space opens at Silom Road, booth setup begins",
      },
      {
        startDateTime: "17:00",
        endDateTime: "17:45",
        title: "Welcome Ceremony",
        description:
          "Traditional Thai blessing ceremony and cultural performances",
      },
      {
        startDateTime: "17:45",
        endDateTime: "18:30",
        title: "Opening Speeches",
        description:
          "Community leaders, activists, and government representatives",
      },
      {
        startDateTime: "18:30",
        endDateTime: "19:00",
        title: "Parade Assembly",
        description: "Participants gather at Silom Soi 2 starting point",
      },
      {
        startDateTime: "19:00",
        endDateTime: "21:00",
        title: "Pride Parade",
        description: "Route: Silom Road → Sathorn → Lumphini Park area",
      },
      {
        startDateTime: "21:00",
        endDateTime: "22:30",
        title: "Main Stage Show",
        description: "Performances by Thai LGBTQ+ artists and allies",
      },
      {
        startDateTime: "22:30",
        endDateTime: "23:00",
        title: "Closing Ceremony",
        description: "Community awards and final performances",
      },
      {
        startDateTime: "23:00",
        endDateTime: "00:00",
        title: "After-Party Kickoff",
        description: "Various venues throughout Silom area",
      },
    ],
    highlights: [],
    gallery: {
      id: "bangkok-pride",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/bangkok/gallery/people/C7sxybqvQkW.jpg"),
          alt: "Drag performers in elaborate Thai-inspired costumes at Bangkok Pride main stage",
          tags: ["people"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/bangkok/gallery/people/kneskate.jpg"),
          alt: "LGBTQ+ activists holding rainbow flags during Bangkok Pride march on Silom Road",
          tags: ["people"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/bangkok/gallery/people/vikromwongsa.jpg"),
          alt: "Community leaders giving speeches at Bangkok Pride opening ceremony",
          tags: ["people"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/bangkok/gallery/people/wilsondaiparis.jpg"),
          alt: "Diverse crowd celebrating with traditional Thai dancers at Bangkok Pride festival",
          tags: ["people"],
        },
      ],
      tags: [{ id: "people", label: "people", color: "#FF0000" }],
    },
    expectedAttendance: 100000,
    tags: [EventTag.FEATURE],
    website: "https://www.bangkokpride.org/",
    links: [
      {
        type: "facebook",
        url: "https://www.facebook.com/bangkokpride.official",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/bangkokpride.official",
      },
      {
        type: "tiktok",
        url: "https://www.tiktok.com/@bangkokprideofficial",
      },
      {
        type: "website",
        url: "https://www.bangkokpride.org/en",
      },
    ],
    transportations: [
      {
        mode: "train",
        title: "BTS Skytrain",
        duration: "2 mins walk from station",
        icon: "train",
        instructions: [
          "Take BTS to Sala Daeng Station",
          "Use Exit 4 or 5",
          "Walk straight onto Silom Road",
          "Pride event area will be clearly visible",
        ],
      },
      {
        mode: "train",
        title: "MRT Subway",
        duration: "5 mins walk from station",
        icon: "subway",
        instructions: [
          "Take MRT to Silom Station",
          "Use Exit 2",
          "Follow signs to Silom Road",
          "Look for Pride flags and decorations",
        ],
      },
      {
        mode: "rideshare",
        title: "Grab/Taxi",
        duration: "Varies with traffic",
        icon: "local-taxi",
        instructions: [
          'Set destination as "Silom Soi 2" or "DJ Station"',
          "Show driver: ถนนสีลม ซอย 2",
          "Expected higher traffic during event",
          "Consider nearby drop-off points if congested",
        ],
      },
      {
        mode: "bus",
        title: "Public Bus",
        duration: "10 mins walk from stop",
        icon: "directions-bus",
        instructions: [
          "Take buses 15, 77, or 504",
          "Alight at Silom Complex stop",
          "Walk towards Silom Soi 2",
          "Follow Pride event signage",
        ],
      },
    ],
  },
  {
    id: "3",
    title: "Hanoi Pride 2025",
    description: `
      Hanoi Pride represents a growing celebration of Vietnam's LGBTQ+ community, marking the evolving attitudes toward gender and sexual diversity in Vietnamese society. As one of Vietnam's most significant LGBTQ+ events, it combines cultural celebration with advocacy for equal rights and recognition.

      Hanoi Pride began as a small gathering in 2012, organized by Hanoi's LGBTQ+ community and supporters. The event has grown steadily, reflecting Vietnam's increasingly open attitude toward LGBTQ+ rights and recognition. While more modest in scale compared to some regional Pride events, it holds particular significance as a platform for visibility and advocacy in Vietnam.

      Hanoi Pride continues to evolve as Vietnam's society becomes more accepting of LGBTQ+ rights and recognition. While maintaining respect for traditional Vietnamese values, the celebration demonstrates the country's growing embrace of diversity and inclusion.
For the most current information about specific events, schedules, and activities for Hanoi Pride 2025, visitors should consult the official event website and social media channels as the event approaches.

    `,
    coverImageUri: require("@/assets/images/screens/events/hanoi/cover.png"),
    images: [],
    type: EventCategory.PARADE,
    status: EventStatus.TENTATIVE,
    startDateTime: "2025-09-21T10:00:00+07:00",
    endDateTime: "2025-09-21T20:00:00+07:00",
    timeZone: TimeZone.ASIA_HOCHIMINH,
    location: {
      coordinates: {
        latitude: 21.0285, // Approximate latitude for Hoàn Kiếm Lake
        longitude: 105.8542, // Approximate longitude for Hoàn Kiếm Lake
      },
      address: {
        country: "Vietnam",
        countryCode: "VN",
        street: "Hoàn Kiếm Lake",
        city: "Hanoi",
        state: null,
        stateCode: null,
        postalCode: null,
        county: null,
      },
    },
    highlights: [],
    schedule: [
      {
        startDateTime: "15:00",
        endDateTime: "16:00",
        title: "Community Fair Opens",
        description:
          "Event space opens at Temple of Literature grounds, organization booths setup",
      },
      {
        startDateTime: "16:00",
        endDateTime: "16:45",
        title: "Opening Ceremony",
        description:
          "Traditional Vietnamese music performance and welcome speeches",
      },
      {
        startDateTime: "16:45",
        endDateTime: "17:30",
        title: "Community Presentations",
        description:
          "Local LGBTQ+ organizations share their work and initiatives",
      },
      {
        startDateTime: "17:30",
        endDateTime: "18:00",
        title: "Bicycle Rally Assembly",
        description:
          "Participants gather with decorated bicycles at starting point",
      },
      {
        startDateTime: "18:00",
        endDateTime: "19:30",
        title: "Pride Bicycle Rally",
        description:
          "Route: Temple of Literature → Hoan Kiem Lake → Thong Nhat Park",
      },
      {
        startDateTime: "19:30",
        endDateTime: "21:00",
        title: "Main Stage Performances",
        description: "Local artists, dancers, and community performers",
      },
      {
        startDateTime: "21:00",
        endDateTime: "21:30",
        title: "Closing Ceremony",
        description: "Community recognition and lantern release ceremony",
      },
      {
        startDateTime: "21:30",
        endDateTime: "23:00",
        title: "Community Gatherings",
        description: "Various venues around Hoan Kiem Lake area",
      },
    ],
    gallery: {
      id: "hanoi-pride",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/hanoi/gallery/people/DAQcF0iS8oE.jpg"),
          alt: "Diverse crowd celebrating with traditional Thai dancers at Bangkok Pride festival",
          tags: ["people"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/hanoi/gallery/people/finlandinhanoi.jpg"),
          alt: "Finnish Embassy representatives showing support at Hanoi Pride celebration",
          tags: ["people"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/hanoi/gallery/people/nhat_wangg.jpg"),
          alt: "Traditional Vietnamese performers in rainbow áo dài at Hanoi Pride opening ceremony",
          tags: ["people"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/hanoi/gallery/people/nutapiwich.jpg"),
          alt: "Community leaders giving speeches at Temple of Literature during Hanoi Pride",
          tags: ["people"],
        },
        {
          id: "5",
          uri: require("@/assets/images/screens/events/hanoi/gallery/swag/all.png"),
          alt: "Complete collection of Hanoi Pride 2025 merchandise including bags, pins, and flags",
          tags: ["swag"],
        },
        {
          id: "6",
          uri: require("@/assets/images/screens/events/hanoi/gallery/swag/bag.png"),
          alt: "Limited edition Hanoi Pride tote bag featuring traditional Vietnamese patterns",
          tags: ["swag"],
        },
        {
          id: "7",
          uri: require("@/assets/images/screens/events/hanoi/gallery/swag/hat.png"),
          alt: "Rainbow-colored baseball cap with Hanoi Pride 2025 logo in Vietnamese",
          tags: ["swag"],
        },
      ],
      tags: [
        { id: "people", label: "people", color: "#FF0000" },
        { id: "swag", label: "swag", color: "#FF0000" },
      ],
    },
    expectedAttendance: 5000,
    tags: [],
    website:
      "https://docs.google.com/forms/d/e/1FAIpQLSckbauG_sbuyXzJPPojKBJpfSkFIhoOoXcpGZfCCXHMQ-EFBQ/viewform",
    links: [
      {
        type: "facebook",
        url: "https://www.facebook.com/hanoipride.vn/?locale=vi_VN",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/hanoipride?fbclid=IwY2xjawIaO09leHRuA2FlbQIxMAABHfm8e0-efx9hZoxlz-x6XLlq1PKfV01R28e7g6LtUBC_n40Q2eofl6zulA_aem_DUwjr4U97HHXSNEyJwNwqA",
      },
      {
        type: "tiktok",
        url: "https://www.tiktok.com/@hanoipride?fbclid=IwY2xjawIaO1tleHRuA2FlbQIxMAABHfivUQ7VL-a3cYyz1C4ZpR6WC7KPiQDSKtII1W-Rg_jRDf6Ww6Vbs1GZWA_aem_hImXNQs2_aFSbqDIi8aSyQ",
      },
      {
        type: "website",
        url: "hanoipride.vn",
      },
    ],
    transportations: [
      {
        mode: "bus",
        title: "Public Bus",
        duration: "5-10 mins walk from stop",
        icon: "directions-bus",
        instructions: [
          "Take bus routes 9, 14, or 22 to Temple of Literature stop",
          "Look for 'Văn Miếu' bus stop signs",
          "Follow event signage to main entrance",
          "Multiple bus routes available from city center",
        ],
      },
      {
        mode: "rideshare",
        title: "Grab/Taxi",
        duration: "Varies with traffic",
        icon: "local-taxi",
        instructions: [
          'Set destination as "Temple of Literature (Văn Miếu)"',
          "Show driver: Văn Miếu - Quốc Tử Giám",
          "Main entrance on Quoc Tu Giam Street",
          "Consider nearby drop-off points during peak times",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "20-30 mins from Old Quarter",
        icon: "directions-walk",
        instructions: [
          "From Hoan Kiem Lake, head south on Le Thai To",
          "Continue on Trang Thi Street",
          "Turn right onto Quoc Tu Giam Street",
          "Event venue will be on your right",
        ],
      },
      {
        mode: "train",
        title: "Metro (Cat Linh - Ha Dong Line)",
        duration: "15 mins walk from station",
        icon: "train",
        instructions: [
          "Take metro to Cat Linh Station",
          "Exit towards Van Mieu Street",
          "Walk south for approximately 1km",
          "Follow Pride event signage",
        ],
      },
    ],
  },
  {
    id: "4",
    title: "Hong Kong Pride 2025",
    description: `
      Hong Kong Pride, also known as Hong Kong Pride Parade (香港同志遊行), represents a significant celebration of the LGBTQ+ community in one of Asia's most international cities. The event showcases Hong Kong's unique position as a meeting point of Eastern and Western cultures while advocating for LGBTQ+ rights and recognition.

      Hong Kong Pride began in 2008 and has grown from a small gathering into a major event attracting thousands of participants from Hong Kong and around the world. The celebration has become increasingly significant as Hong Kong's LGBTQ+ community continues to advocate for greater recognition and legal protections.

    `,
    coverImageUri: require("@/assets/images/screens/events/hongkong/cover.jpg"),
    images: [],
    type: EventCategory.PARADE,
    status: EventStatus.TENTATIVE,
    startDateTime: "2025-11-15T14:00:00+08:00",
    endDateTime: "2025-11-15T18:00:00+08:00",
    timeZone: TimeZone.ASIA_HONGKONG,
    location: {
      coordinates: {
        latitude: 22.3008, // Latitude for West Kowloon Cultural District Art Park
        longitude: 114.159, // Longitude for West Kowloon Cultural District Art Park
      },
      address: {
        country: "Hong Kong",
        countryCode: "HK",
        street: "West Kowloon Cultural District Art Park",
        city: "Kowloon",
        state: null,
        stateCode: null,
        postalCode: null,
        county: null,
      },
    },
    schedule: [
      {
        startDateTime: "12:00",
        endDateTime: "13:00",
        title: "Pride Village Opens",
        description:
          "Event space opens at Victoria Park, organization booths setup",
      },
      {
        startDateTime: "13:00",
        endDateTime: "13:45",
        title: "Opening Ceremony",
        description: "Welcome speeches from organizers and community leaders",
      },
      {
        startDateTime: "13:45",
        endDateTime: "14:30",
        title: "Cultural Performances",
        description: "Local LGBTQ+ artists and allies showcase",
      },
      {
        startDateTime: "14:30",
        endDateTime: "15:00",
        title: "Parade Assembly",
        description: "Participants gather at Victoria Park main lawn",
      },
      {
        startDateTime: "15:00",
        endDateTime: "17:00",
        title: "Pride Parade",
        description: "Route: Victoria Park → Causeway Bay → Wan Chai → Central",
      },
      {
        startDateTime: "17:00",
        endDateTime: "19:00",
        title: "Main Stage Show",
        description:
          "International and local performers, community presentations",
      },
      {
        startDateTime: "19:00",
        endDateTime: "19:30",
        title: "Closing Ceremony",
        description: "Community awards and final performances",
      },
      {
        startDateTime: "19:30",
        endDateTime: "23:00",
        title: "Pride Celebrations",
        description: "Various venues throughout Central and Lan Kwai Fong",
      },
    ],
    highlights: [],
    gallery: {
      id: "hk-pride-parade",
      title: "Hong Kong Pride Parade 2025",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/hongkong/gallery/people/elements_choir.jpg"),
          alt: "Elements Choir performing at Hong Kong Pride opening ceremony in Victoria Park",
          tags: ["people"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/hongkong/gallery/people/photo2.jpg"),
          alt: "Diverse crowd marching with rainbow flags through Causeway Bay during Hong Kong Pride",
          tags: ["people"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/hongkong/gallery/people/photo3.jpg"),
          alt: "LGBTQ+ activists and allies gathering at Victoria Park for Hong Kong Pride parade",
          tags: ["people"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/hongkong/gallery/people/photo4.jpg"),
          alt: "Community leaders giving speeches on the main stage at Hong Kong Pride festival",
          tags: ["people"],
        },
        {
          id: "5",
          uri: require("@/assets/images/screens/events/hongkong/gallery/swag/bag.jpeg"),
          alt: "Official Hong Kong Pride 2025 tote bag featuring skyline design and rainbow motif",
          tags: ["swag"],
        },
        {
          id: "6",
          uri: require("@/assets/images/screens/events/hongkong/gallery/swag/souvenir1.jpg"),
          alt: "Limited edition Hong Kong Pride pins and badges with traditional Chinese characters",
          tags: ["swag"],
        },
        {
          id: "7",
          uri: require("@/assets/images/screens/events/hongkong/gallery/swag/souvenir2.jpg"),
          alt: "Collection of Hong Kong Pride merchandise including flags and wristbands",
          tags: ["swag"],
        },
        {
          id: "8",
          uri: require("@/assets/images/screens/events/hongkong/gallery/swag/tag.jpeg"),
          alt: "Special edition Hong Kong Pride 2025 event pass with rainbow gradient design",
          tags: ["swag"],
        },
      ],
      tags: [
        { id: "people", label: "people", color: "#FF0000" },
        { id: "swag", label: "swag", color: "#FF0000" },
      ],
    },
    expectedAttendance: 15000,
    tags: [],
    website: "https://hkpride.net/",
    links: [
      {
        type: "facebook",
        url: "https://www.facebook.com/hkpride",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/hkprideparade",
      },
      {
        type: "twitter",
        url: "https://x.com/HKPrideParade",
      },
      {
        type: "website",
        url: "https://hkpride.net/",
      },
    ],
    transportations: [
      {
        mode: "train",
        title: "MTR",
        duration: "3 mins walk from station",
        icon: "train",
        instructions: [
          "Take MTR to Causeway Bay Station",
          "Use Exit E towards Victoria Park",
          "Follow signs to Victoria Park main entrance",
          "Pride event area will be visible from entrance",
        ],
      },
      {
        mode: "train",
        title: "Hong Kong Tram",
        duration: "5 mins walk from stop",
        icon: "tram",
        instructions: [
          "Take tram to Victoria Park stop",
          "Exit onto Causeway Bay Road",
          "Cross at designated crossing",
          "Enter Victoria Park main gate",
        ],
      },
      {
        mode: "bus",
        title: "Public Bus",
        duration: "7 mins walk from stop",
        icon: "directions-bus",
        instructions: [
          "Take routes 2, 8, 77, or 108 to Victoria Park",
          "Alight at Victoria Park Bus Stop",
          "Cross at pedestrian crossing",
          "Follow Pride event signage",
        ],
      },
      {
        mode: "rideshare",
        title: "Taxi",
        duration: "Direct drop-off",
        icon: "local-taxi",
        instructions: [
          'Show destination: "Victoria Park" (維多利亞公園)',
          "Request drop-off at main entrance",
          "Multiple drop-off points available",
          "Expect increased traffic during event",
        ],
      },
      {
        mode: "bus",
        title: "Mini Bus",
        duration: "5 mins walk from stop",
        icon: "airport-shuttle",
        instructions: [
          "Take green minibus routes 14M or 24A",
          "Request stop at Victoria Park",
          "Follow crowd to event entrance",
          "Available from various districts",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "15-20 mins from Causeway Bay",
        icon: "directions-walk",
        instructions: [
          "From Times Square, walk east on Hennessy Road",
          "Turn left onto Victoria Park Road",
          "Enter park through main gates",
          "Follow Pride event signage",
        ],
      },
    ],
  },
  {
    id: "5",
    title: "Singapore Pride 2025",
    description: `
      Pink Dot SG represents Singapore's unique approach to LGBTQ+ celebration and advocacy. Held annually at Hong Lim Park, it has become one of Singapore's most significant civil society events, characterized by its peaceful, organized nature and strong community focus.
    
      Since its inception in 2009, Pink Dot has grown from a small gathering into a major event that draws thousands of participants. The event takes its name from the combining of "Pink," a color representing the LGBTQ+ community, and "Red Dot," Singapore's nickname, symbolizing the harmonious integration of LGBTQ+ identity with Singaporean society.

    `,
    coverImageUri: require("@/assets/images/screens/events/singapore/cover.jpg"),
    images: [],
    type: EventCategory.PARADE,
    status: EventStatus.TENTATIVE,
    startDateTime: "2025-06-28T16:00:00+08:00",
    endDateTime: "2025-06-28T21:00:00+08:00",
    timeZone: TimeZone.ASIA_SINGAPORE,
    location: {
      coordinates: {
        latitude: 1.2865, // Latitude for Hong Lim Park
        longitude: 103.8484, // Longitude for Hong Lim Park
      },
      address: {
        country: "Singapore",
        countryCode: "SG",
        street: "Hong Lim Park",
        city: "Singapore",
        state: null,
        stateCode: null,
        postalCode: null,
        county: null,
      },
    },
    schedule: [
      {
        startDateTime: "14:00",
        endDateTime: "15:00",
        title: "Community Village Opens",
        description:
          "Entry begins at Hong Lim Park, registration and ID verification",
      },
      {
        startDateTime: "15:00",
        endDateTime: "15:45",
        title: "Opening Ceremony",
        description: "Welcome addresses and community leaders' speeches",
      },
      {
        startDateTime: "15:45",
        endDateTime: "16:30",
        title: "Community Showcase",
        description:
          "Local LGBTQ+ organizations present their work and initiatives",
      },
      {
        startDateTime: "16:30",
        endDateTime: "17:30",
        title: "Concert Segment",
        description: "Performances by local artists and community groups",
      },
      {
        startDateTime: "17:30",
        endDateTime: "18:15",
        title: "Community Stories",
        description: "Sharing sessions and testimonials from community members",
      },
      {
        startDateTime: "18:15",
        endDateTime: "19:00",
        title: "Pink Dot Formation",
        description: "Gathering and organization of human pink dot formation",
      },
      {
        startDateTime: "19:00",
        endDateTime: "19:30",
        title: "Light-Up Moment",
        description: "Iconic pink dot light-up and aerial photo moment",
      },
      {
        startDateTime: "19:30",
        endDateTime: "20:00",
        title: "Closing Ceremony",
        description: "Final messages and community celebration",
      },
    ],
    highlights: [],
    gallery: {
      id: "singapore-pride",
      title: "Pink Dot SG 2025",
      images: [],
      tags: [{ id: "people", label: "people", color: "#FF0000" }],
    },
    expectedAttendance: 20000,
    tags: [],
    website: "https://www.pinkdot.sg",
    links: [],
    transportations: [
      {
        mode: "train",
        title: "MRT",
        duration: "3 mins walk from station",
        icon: "train",
        instructions: [
          "Take MRT to Clarke Quay Station (NE5)",
          "Use Exit E towards Hong Lim Park",
          "Follow signage to event entrance",
          "Please have identification ready",
        ],
      },
      {
        mode: "bus",
        title: "Public Bus",
        duration: "5 mins walk from stop",
        icon: "directions-bus",
        instructions: [
          "Take buses 51, 186, or 174 to New Bridge Road",
          "Alight at Hong Lim Complex stop",
          "Cross at designated crossing",
          "Follow event signage to entrance",
        ],
      },
      {
        mode: "rideshare",
        title: "Grab/Taxi",
        duration: "Direct drop-off",
        icon: "local-taxi",
        instructions: [
          'Set destination as "Hong Lim Park" or "Speakers Corner"',
          "Drop-off point at New Bridge Road entrance",
          "Follow crowd to registration area",
          "Note: Expect higher fares during event",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "10 mins from Chinatown",
        icon: "directions-walk",
        instructions: [
          "From Chinatown MRT, head towards New Bridge Road",
          "Walk along South Bridge Road",
          "Turn right at Hong Lim Park",
          "Event entrance will be visible",
        ],
      },
    ],
  },
  {
    id: "6",
    title: "Phuket Pride 2025",
    description: `
      Phuket Pride represents a vibrant celebration of Thailand's LGBTQ+ community in one of the country's most popular tourist destinations. The event combines local Thai culture with international influences, creating a unique celebration that attracts participants from around the world.

      Phuket has long been known for its LGBTQ+-friendly atmosphere, with Pride celebrations becoming an increasingly significant part of the island's cultural calendar. The event showcases both Thailand's traditional acceptance of gender diversity and its modern approach to LGBTQ+ rights and tourism.

    `,
    coverImageUri: require("@/assets/images/screens/events/phuket/cover.jpg"),
    images: [],
    type: EventCategory.PARADE,
    status: EventStatus.TENTATIVE,
    startDateTime: "2025-06-28T16:00:00+07:00",
    endDateTime: "2025-06-28T22:00:00+07:00",
    timeZone: TimeZone.ASIA_BANGKOK,
    location: {
      coordinates: {
        latitude: 7.8906,
        longitude: 98.2961,
      },
      address: {
        country: "Thailand",
        countryCode: "TH",
        street: "Patong Beach",
        city: "Patong",
        state: "Phuket",
        stateCode: "83",
        postalCode: null,
        county: "Kathu",
      },
    },
    schedule: [
      {
        startDateTime: "16:00",
        endDateTime: "17:00",
        title: "Pride Village Opens",
        description:
          "Event space opens at Patong Beach, vendor and booth setup",
      },
      {
        startDateTime: "17:00",
        endDateTime: "17:45",
        title: "Opening Ceremony",
        description: "Traditional Thai blessing ceremony and welcome speeches",
      },
      {
        startDateTime: "17:45",
        endDateTime: "18:30",
        title: "Cultural Performances",
        description: "Thai dance shows and local LGBTQ+ performers",
      },
      {
        startDateTime: "18:30",
        endDateTime: "19:00",
        title: "Parade Assembly",
        description: "Participants gather at Patong Beach starting point",
      },
      {
        startDateTime: "19:00",
        endDateTime: "20:30",
        title: "Pride Parade",
        description: "Route: Patong Beach Road → Paradise Complex → Beach Road",
      },
      {
        startDateTime: "20:30",
        endDateTime: "22:00",
        title: "Main Stage Show",
        description: "International performers, drag shows, and live music",
      },
      {
        startDateTime: "22:00",
        endDateTime: "22:30",
        title: "Closing Ceremony",
        description: "Community awards and final performances",
      },
      {
        startDateTime: "22:30",
        endDateTime: "02:00",
        title: "Beach Party",
        description:
          "Various venues throughout Paradise Complex and beach area",
      },
    ],
    highlights: [],
    gallery: {
      id: "phuket-pride",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/phuket/gallery/people/ponghhhn.jpg"),
          alt: "",
          tags: ["people"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/phuket/gallery/people/tarworkaholic.jpg"),
          alt: "",
          tags: ["people"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/phuket/gallery/people/photo3.jpg"),
          alt: "",
          tags: ["people"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/phuket/gallery/people/photo4.jpg"),
          alt: "",
          tags: ["people"],
        },
      ],
      tags: [{ id: "people", label: "people", color: "#FF0000" }],
    },
    expectedAttendance: 8000,
    tags: [],
    website: "https://www.travelgay.com/venue/phuket-pride-festival-2012",
    links: [],
    transportations: [
      {
        mode: "car",
        title: "Taxi/Grab",
        duration: "Varies by location",
        icon: "local-taxi",
        instructions: [
          'Set destination as "Patong Beach Pride Event" or "Paradise Complex"',
          "Show driver: หาดป่าตอง (Patong Beach)",
          "Multiple drop-off points available",
          "Use ride-hailing apps for fixed prices",
        ],
      },
    ],
  },
  {
    id: "7",
    title: "Metro Manila Pride 2025",
    description: `
      Metro Manila Pride represents one of Southeast Asia's most vibrant LGBTQ+ celebrations, reflecting the Philippines' unique blend of cultural influences and growing advocacy for LGBTQ+ rights. The event brings together communities from across the National Capital Region in a display of solidarity and celebration.

      Metro Manila Pride has grown from its humble beginnings to become a significant cultural event in the Philippines. The celebration continues the country's tradition of LGBTQ+ activism while embracing modern forms of advocacy and expression.
    `,
    coverImageUri: require("@/assets/images/screens/events/manila/cover.jpg"),
    images: [],
    type: EventCategory.PARADE,
    status: EventStatus.TENTATIVE,
    startDateTime: "2025-06-29T10:00:00+08:00",
    endDateTime: "2025-06-29T18:00:00+08:00",
    timeZone: TimeZone.ASIA_MANILA,
    location: {
      coordinates: {
        latitude: 14.5995, // Approximate latitude for Manila, Philippines
        longitude: 120.9842, // Approximate longitude for Manila, Philippines
      },
      address: {
        country: "Philippines",
        countryCode: "PH",
        street: "Roxas Boulevard",
        city: "Manila",
        state: "Metro Manila",
        stateCode: null,
        postalCode: null,
        county: null,
      },
    },
    schedule: [
      {
        startDateTime: "16:00",
        endDateTime: "17:00",
        title: "Pride Village Opens",
        description:
          "Event space opens at Patong Beach, vendor and booth setup",
      },
      {
        startDateTime: "17:00",
        endDateTime: "17:45",
        title: "Opening Ceremony",
        description: "Traditional Thai blessing ceremony and welcome speeches",
      },
      {
        startDateTime: "17:45",
        endDateTime: "18:30",
        title: "Cultural Performances",
        description: "Thai dance shows and local LGBTQ+ performers",
      },
      {
        startDateTime: "18:30",
        endDateTime: "19:00",
        title: "Parade Assembly",
        description: "Participants gather at Patong Beach starting point",
      },
      {
        startDateTime: "19:00",
        endDateTime: "20:30",
        title: "Pride Parade",
        description: "Route: Patong Beach Road → Paradise Complex → Beach Road",
      },
      {
        startDateTime: "20:30",
        endDateTime: "22:00",
        title: "Main Stage Show",
        description: "International performers, drag shows, and live music",
      },
      {
        startDateTime: "22:00",
        endDateTime: "22:30",
        title: "Closing Ceremony",
        description: "Community awards and final performances",
      },
      {
        startDateTime: "22:30",
        endDateTime: "02:00",
        title: "Beach Party",
        description:
          "Various venues throughout Paradise Complex and beach area",
      },
    ],
    highlights: [],
    gallery: {
      id: "manila-pride",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/manila/gallery/people/photo1.jpg"),
          alt: "Colorful parade floats and performers at Metro Manila Pride marching down Roxas Boulevard",
          tags: ["people"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/manila/gallery/people/photo2.jpg"),
          alt: "LGBTQ+ youth groups and student organizations carrying banners at Manila Pride march",
          tags: ["people"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/manila/gallery/people/photo3.jpg"),
          alt: "Traditional Filipino performers in rainbow-themed costumes at Manila Pride opening ceremony",
          tags: ["people"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/manila/gallery/people/photo4.jpg"),
          alt: "Community activists giving empowering speeches at Metro Manila Pride main stage",
          tags: ["people"],
        },
      ],
      tags: [{ id: "people", label: "people", color: "#FF0000" }],
    },
    expectedAttendance: 200000,
    tags: [EventTag.FEATURE],
    website: "https://www.instagram.com/mmprideorg/",
    links: [
      {
        type: "instagram",
        url: "https://www.instagram.com/mmprideorg/",
      },
    ],
    transportations: [
      {
        mode: "train",
        title: "LRT-2",
        duration: "15 mins walk from station",
        icon: "train",
        instructions: [
          "Take LRT-2 to Santolan Station",
          "Exit towards Marikina side",
          "Walk to Marikina Sports Center",
          "Follow event signage and crowd",
        ],
      },
      {
        mode: "car",
        title: "Grab/Taxi",
        duration: "Depends on traffic",
        icon: "local-taxi",
        instructions: [
          'Set destination as "Marikina Sports Center"',
          "Show driver: Marikina Sports Center, Sumulong Highway",
          "Consider traffic during event hours",
          "Book early during peak times",
        ],
      },
      {
        mode: "bus",
        title: "City Bus",
        duration: "Varies by route",
        icon: "directions-bus",
        instructions: [
          "Take buses bound for Marikina",
          "Alight at Sumulong Highway",
          "Walk towards Sports Center",
          "Multiple bus routes available",
        ],
      },
    ],
  },
  {
    id: "8",
    title: "Seoul Pride Parade",
    description: `
      Seoul Pride, officially known as the Seoul Queer Culture Festival (서울퀴어문화축제), represents one of East Asia's largest LGBTQ+ celebrations. The event combines advocacy for LGBTQ+ rights with cultural celebration in the heart of South Korea's capital.

      Seoul Pride began in 2000 as a small gathering and has grown into a major event attracting tens of thousands of participants. Despite facing various challenges, the festival has become an important platform for visibility and advocacy in South Korean society.

    `,
    type: EventCategory.PARADE,
    status: EventStatus.TENTATIVE,
    coverImageUri: require("@/assets/images/screens/events/seoul/cover.jpg"),
    images: [],
    startDateTime: "2025-05-31T10:00:00+09:00",
    endDateTime: "2025-05-31T17:30:00+09:00",
    timeZone: TimeZone.ASIA_SEOUL,
    location: {
      coordinates: {
        latitude: 37.5665, // Approximate latitude for Seoul Plaza
        longitude: 126.978, // Approximate longitude for Seoul Plaza
      },
      address: {
        country: "South Korea",
        countryCode: "KR",
        street: "Sejong-daero",
        city: "Seoul",
        state: null,
        stateCode: null,
        postalCode: null,
        county: null,
      },
    },
    schedule: [
      {
        startDateTime: "11:00",
        endDateTime: "12:00",
        title: "Festival Village Opens",
        description: "Event space opens at Seoul Plaza, booth setup begins",
      },
      {
        startDateTime: "12:00",
        endDateTime: "12:45",
        title: "Opening Ceremony",
        description: "Welcome speeches and traditional Korean performances",
      },
      {
        startDateTime: "12:45",
        endDateTime: "13:30",
        title: "Community Showcase",
        description: "Presentations by LGBTQ+ organizations and allies",
      },
      {
        startDateTime: "13:30",
        endDateTime: "14:30",
        title: "Main Stage Performances",
        description: "K-pop artists, drag performers, and community talents",
      },
      {
        startDateTime: "14:30",
        endDateTime: "15:00",
        title: "Parade Assembly",
        description: "Participants gather at Seoul Plaza for parade formation",
      },
      {
        startDateTime: "15:00",
        endDateTime: "17:00",
        title: "Pride Parade",
        description: "Route: Seoul Plaza → Euljiro → Myeongdong → City Hall",
      },
      {
        startDateTime: "17:00",
        endDateTime: "19:00",
        title: "Evening Concert",
        description: "Live performances and community celebrations",
      },
      {
        startDateTime: "19:00",
        endDateTime: "20:00",
        title: "Closing Ceremony",
        description: "Final speeches and solidarity gathering",
      },
    ],
    highlights: [],
    gallery: {
      id: "seoul-pride",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/seoul/gallery/people/photo1.jpg"),
          alt: "K-pop performers and drag artists on the main stage at Seoul Plaza during Seoul Pride",
          tags: ["people"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/seoul/gallery/people/photo2.jpg"),
          alt: "LGBTQ+ activists and allies marching with rainbow flags through Euljiro during Seoul Pride parade",
          tags: ["people"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/seoul/gallery/people/photo3.jpg"),
          alt: "Traditional Korean performers in rainbow hanbok at Seoul Queer Culture Festival opening ceremony",
          tags: ["people"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/seoul/gallery/people/photo4.jpg"),
          alt: "Community organizations and advocacy groups with banners at Seoul Plaza Pride festival",
          tags: ["people"],
        },
      ],
      tags: [{ id: "people", label: "people", color: "#FF0000" }],
    },
    expectedAttendance: 3500,
    tags: [],
    website: "https://www.sqcf.org/",
    links: [
      {
        type: "website",
        url: "https://www.sqcf.org/",
      },
    ],
    transportations: [
      {
        mode: "train",
        title: "Seoul Metro",
        duration: "2 mins walk from station",
        icon: "train",
        instructions: [
          "Take subway to City Hall Station (Lines 1 & 2)",
          "Use Exit 5 towards Seoul Plaza",
          "Follow signs to event entrance",
          "Multiple subway lines provide easy access",
        ],
      },
      {
        mode: "bus",
        title: "City Bus",
        duration: "5 mins walk from stop",
        icon: "directions-bus",
        instructions: [
          "Take buses stopping at City Hall (시청)",
          "Multiple bus routes available",
          "Look for Seoul Plaza signs",
          "Use transportation card for easy payment",
        ],
      },
      {
        mode: "car",
        title: "Taxi/Kakao T",
        duration: "Depends on traffic",
        icon: "local-taxi",
        instructions: [
          'Show destination: "Seoul Plaza" (서울광장)',
          "Use Kakao T app for easy booking",
          "Multiple drop-off points available",
          "Consider nearby streets during peak times",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "15-20 mins from nearby areas",
        icon: "directions-walk",
        instructions: [
          "From Myeongdong: Head west towards City Hall",
          "From Gwanghwamun: Head south to Seoul Plaza",
          "Well-marked pedestrian paths available",
          "Follow crowd flow to event area",
        ],
      },
    ],
  },
  {
    id: "10",
    title: "San Francisco Pride and Festival",
    description: `San Francisco Pride stands as one of the largest and oldest LGBTQ+ celebrations in the world, drawing participants from across the globe. The event features the iconic parade down Market Street, live entertainment, and community events at Civic Center Plaza.

The celebration has played a crucial role in advancing LGBTQ+ rights in the United States, contributing to milestone achievements in the movement. The 2025 celebration continues San Francisco Pride's tradition of combining festive celebration with social activism.`,
    type: EventCategory.PARADE,
    status: EventStatus.CONFIRMED,
    coverImageUri: require("@/assets/images/screens/events/sanfrancisco/cover.jpg"),
    images: [],
    startDateTime: "2025-06-29T10:00:00-07:00",
    endDateTime: "2025-06-29T18:00:00-07:00",
    timeZone: TimeZone.AMERICA_LOS_ANGELES,
    location: {
      coordinates: {
        latitude: 37.7953, // Latitude for Embarcadero Plaza
        longitude: -122.3956, // Longitude for Embarcadero Plaza
      },
      address: {
        country: "United States",
        countryCode: "US",
        street: "Market St at Embarcadero Plaza",
        city: "San Francisco",
        state: "California",
        stateCode: "CA",
        postalCode: "94105",
        county: "San Francisco",
      },
    },
    schedule: [
      {
        startDateTime: "10:00",
        endDateTime: "14:00",
        title: "Pride Parade",
        description:
          "Iconic parade down Market Street, ending around Market Street and 9th Street",
      },
      {
        startDateTime: "11:00",
        endDateTime: "18:00",
        title: "Civic Center Plaza Festival",
        description: "Live entertainment and community booths",
      },
    ],
    highlights: [
      {
        rank: 1,
        title: "Iconic Parade",
        description:
          "The famous parade down Market Street. The theme is Queer Joy is Resistance",
      },
      {
        rank: 2,
        title: "Main Stage Performances",
        description:
          "Live shows and headline acts at the heart of Civic Center",
      },
      {
        rank: 3,
        title: "LGBTQ+ Street Fair",
        description:
          "Over 300 artists and exhibitors celebrating queer culture",
      },
      {
        rank: 4,
        title: "18+ Experience Zone",
        description: "Adult-only area with curated performances and vendors",
      },
      {
        rank: 5,
        title: "Community Stages & Spaces",
        description:
          "Local talent, drag, dance, and spoken word across multiple stages",
      },
    ],
    gallery: {
      id: "sf-pride",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/sanfrancisco/gallery/photo1.jpg"),
          alt: "A vibrant participant at the San Francisco Pride Parade 2022 stands on a parade float, wearing a colorful feathered costume with bright orange pants and a decorated top. She holds a smartphone and appears to be cheering or singing, with a joyful expression. The float is adorned with red, black, and green balloons. Tall city buildings and other parade-goers are visible in the background under a clear blue sky.",
          tags: ["people", "parade"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/sanfrancisco/gallery/photo2.jpg"),
          alt: "A joyful participant in the San Francisco Pride Parade 2022 roller skates down the street, raising a Progress Pride flag high in one hand and holding a drink in the other. They wear leopard-print roller skates with purple pom-poms, blue knee-high socks, a colorful cap, and sunglasses. Other parade-goers in rainbow attire march alongside them, some waving Pride and bisexual flags. A large rainbow flag blurs across the foreground, and tall city buildings line the background under a sunny sky.",
          tags: ["people", "parade"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/sanfrancisco/gallery/photo3.jpg"),
          alt: "Member of the San Francisco Lesbian Gay Freedom Band marching at Pride Parade 2022, wearing a black uniform with red trim and a traditional band cap adorned with rainbow-colored plume, carrying a large bass drum featuring the band's logo with rainbow pride flag colors and a heart design, with crowds and city buildings visible in the background",
          tags: ["people", "parade"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/sanfrancisco/gallery/photo4.jpg"),
          alt: "A lively crowd fills a city street during the San Francisco Pride Parade 2022, celebrating amid a shower of blue and gold confetti. People wear colorful clothing, some in teal and gold sports gear, and many raise their hands in excitement. Trees lining the street are decorated with streamers and rainbow flags, and buildings display parade banners. A float with seated participants can be seen on the right, and the atmosphere is festive and vibrant under a clear blue sky.",
          tags: ["people"],
        },
      ],
      tags: [
        { id: "people", label: "people", color: "#FF0000" },
        { id: "map", label: "map", color: "#00FF00" },
        { id: "parade", label: "parade", color: "#FF00FF" },
      ],
    },
    expectedAttendance: 1000000,
    tags: [EventTag.OFFICIAL, EventTag.FEATURE],
    website: "https://sfpride.org",
    links: [
      {
        type: "website",
        url: "https://sfpride.org",
      },
      {
        type: "facebook",
        url: "https://www.facebook.com/SanFranciscoPride/",
      },
      {
        type: "instagram",
        url: "https://instagram.com/sanfranciscopride/",
      },
      {
        type: "youtube",
        url: "https://www.youtube.com/@sfpride-official",
      },
    ],
    transportations: [
      {
        mode: "train",
        title: "BART",
        duration: "minutes walk from station",
        icon: "train",
        instructions: [
          "Take BART to any of the following stations along the parade route: Embarcadero, Montgomery St, Powell St, or Civic Center.",
        ],
      },
      {
        mode: "rideshare",
        title: "Uber/Lyft",
        duration: "Varies with traffic",
        icon: "local-taxi",
        instructions: [
          'Set your destination to a nearby location just off Market Street, between "Civic Center Plaza" and "Embarcadero Plaza"',
          "Avoid selecting a spot directly on the parade route due to road closures",
          "Expect significant traffic and allow extra time for delays and detours",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "Varies based on your location",
        icon: "directions-walk",
        instructions: [
          "If you are in South of Market (SoMa), Financial District, Downtown / Civic Center, walking may be a viable option to reach the parade route or viewing spots.",
          "Be prepared for large pedestrian crowds and wear comfortable shoes.",
        ],
      },
    ],
  },
  {
    id: "11",
    title: "New York City Pride March and PrideFest",
    description: `The NYC Pride March is one of the largest and most historic LGBTQIA+ Pride events in the world, taking place annually in New York City. It commemorates the Stonewall Uprising of June 1969, which is widely considered the catalyst for the modern LGBTQIA+ rights movement. The first March, then called Christopher Street Liberation Day, was held in 1970, one year after the Stonewall Riots.

The March is a vibrant civil rights demonstration and a joyous celebration of identity, diversity, and community. It serves as a powerful platform for advocacy, raising awareness about ongoing LGBTQIA+ issues, and honoring those who have fought for equality, as well as remembering those lost to illness, violence, and neglect.

Millions of participants and spectators from around the globe gather for this iconic event, which sees colorful floats, diverse marching contingents, community groups, and activists take to the streets of Manhattan. The 2025 theme is "Rise Up: Pride in Protest," reflecting the movement's origins and calling for continued action and solidarity.
    `,
    type: EventCategory.PARADE,
    status: EventStatus.TENTATIVE,
    coverImageUri: require("@/assets/images/screens/events/nyc/cover.jpg"),
    images: [],
    startDateTime: "2025-06-29T11:00:00-04:00",
    endDateTime: "2025-06-29T18:00:00-04:00",
    timeZone: TimeZone.AMERICA_NEW_YORK,
    location: {
      coordinates: {
        latitude: 40.7435, // Latitude for 5th Avenue & 26th Street
        longitude: -73.988, // Longitude for 5th Avenue & 26th Street
      },
      address: {
        country: "United States",
        countryCode: "US",
        street: "5th Ave & 26th St",
        city: "New York",
        state: "NY",
        stateCode: "NY",
        postalCode: "10010",
        county: "New York County",
      },
    },
    schedule: [
      {
        startDateTime: "12:00",
        endDateTime: "13:30",
        title: "NYC Pride March",
        description:
          "Starting at 26th St & 5th Ave, dispersing at 15th St & 7th Ave",
      },
      {
        startDateTime: "11:00",
        endDateTime: "18:00",
        title: "PrideFest Street Fair",
        description:
          "LGBTQIA+ street fair in Greenwich Village featuring exhibitors, food, live performances at StageFest, local shopping, and family-friendly fun — all in celebration of equality. Free and open to all!",
      },
    ],
    highlights: [
      {
        rank: 1,
        title: "2025 Theme: Rise Up",
        description: 'The 2025 theme: "Rise Up: Pride in Protest."',
      },
      {
        rank: 2,
        title: "Diverse Participants & Floats",
        description:
          "Thousands of diverse participants and colorful floats from various community groups, organizations, and allies.",
      },
      {
        rank: 3,
        title: "Historic Stonewall Route",
        description:
          "Marching past the historic Stonewall National Monument, the birthplace of the modern LGBTQIA+ rights movement.",
      },
      {
        rank: 4,
        title: "Global Spectacle",
        description:
          "One of the largest Pride celebrations globally, attracting millions of spectators.",
      },
      {
        rank: 5,
        title: "Powerful Display",
        description:
          "A powerful display of LGBTQIA+ resilience, community, and advocacy.",
      },
    ],
    gallery: {
      id: "nyc-pride",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/nyc/gallery/photo1.jpg"),
          alt: "A group of people from Q-Wave walking together in NYC Pride March",
          tags: ["people", "parade"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/nyc/gallery/photo2.jpg"),
          alt: "A group of people smiling and posing for a photograph together at the NYC Pride celebration",
          tags: ["people"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/nyc/gallery/photo3.jpg"),
          alt: "A person on roller skates holds an big LGBTQ Pride peace flag at NYC Pride March with a crowd watching behind a fence",
          tags: ["people", "parade"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/nyc/gallery/photo4.jpg"),
          alt: "Women with Pride flags and fans smile and converse behind a fence at NYC Pride March",
          tags: ["people"],
        },
        {
          id: "5",
          uri: require("@/assets/images/screens/events/nyc/gallery/photo5.jpg"),
          alt: "A group of people from The Trevor Project walking at NYC Pride March",
          tags: ["people", "parade"],
        },
        {
          id: "6",
          uri: require("@/assets/images/screens/events/nyc/gallery/photo6.jpg"),
          alt: "People on a Macy's float at the NYC Pride March hold Pride flags and signs, waving to the crowd",
          tags: ["people", "parade"],
        },
      ],
      tags: [
        { id: "people", label: "people", color: "#FF0000" },
        { id: "map", label: "map", color: "#00FF00" },
        { id: "parade", label: "parade", color: "#FFFF00" },
      ],
    },
    expectedAttendance: 2000000,
    tags: [EventTag.OFFICIAL, EventTag.FEATURE],
    website: "https://www.nycpride.org/",
    links: [
      {
        type: "website",
        url: "https://www.nycpride.org/",
      },
      {
        type: "facebook",
        url: "https://www.facebook.com/NYCPride/",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/nycpride/",
      },
      {
        type: "twitter",
        url: "https://twitter.com/NYCPride",
      },
      {
        type: "youtube",
        url: "https://www.youtube.com/nycpride",
      },
    ],
    transportations: [
      {
        mode: "train",
        title: "NYC Subway",
        duration: "Varies, short walk from multiple stations",
        icon: "train",
        instructions: [
          "For March Start (26th St & 5th Ave): Use 23rd St Station (N, Q, R, W, F, M, PATH) or 28th St Station (N, R, W or 6).",
          "For March Route & End (Greenwich Village/Chelsea): Stations along 5th Ave, Christopher St (1), 7th Ave (1, 2, 3), 14th St (Multiple Lines).",
          "Check MTA website (mta.info) for weekend service changes and best routes.",
          "Expect large crowds at stations near the route; consider stations a few blocks away.",
        ],
      },
      {
        mode: "bus",
        title: "NYC Bus",
        duration: "Varies, stops may be affected by street closures",
        icon: "directions-bus",
        instructions: [
          "Numerous bus routes cross Manhattan. However, routes along 5th Ave, Christopher St, and 7th Ave will be significantly impacted.",
          "Check MTA bus schedules and plan for detours.",
        ],
      },
      {
        mode: "car",
        title: "Car / Ride-share",
        duration:
          "Not recommended due to extensive street closures and traffic",
        icon: "local-taxi",
        instructions: [
          "Driving and parking will be extremely difficult. Many streets will be closed.",
          "If using ride-sharing, expect surge pricing and limited access to the immediate area.",
          "Consider being dropped off several blocks away from the parade route.",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "Varies based on starting point",
        icon: "directions-walk",
        instructions: [
          "If you are in Manhattan, walking may be a viable option to reach the parade route or viewing spots.",
          "Be prepared for large pedestrian crowds and wear comfortable shoes.",
        ],
      },
    ],
  },
  {
    id: "12",
    title: "The Philly Pride March & Festival",
    description: `Philadelphia Pride March & Festival 2025, themed 'Philly L.U.V. (Legacy, Unity and Visibility),' is a vibrant celebration of the LGBTQ+ community. This year's event holds special significance, marking the 50th anniversary of former Pennsylvania Governor Milton Shapp signing an executive order prohibiting discrimination against LGBTQ+ state employees.

The day begins with a community march, an ode to the historic Annual Reminder Day demonstrations of the 1960s, calling for continued LGBTQ+ visibility and solidarity. The march features the spectacular 600-foot Progress Pride flag, the largest Pride flag in Philadelphia’s history.

Following the march, the festival unfolds in the heart of Philadelphia's Gayborhood, offering an expanded footprint with over 200 vendors, diverse food trucks, live performances across multiple stages and zones, and community organization booths. It's a day of joyous celebration, activism, and community connection.`,
    type: EventCategory.PARADE,
    status: EventStatus.CONFIRMED,
    coverImageUri: require("@/assets/images/screens/events/philadelphia/cover.jpg"),
    images: [],
    startDateTime: "2025-06-01T10:30:00-04:00",
    endDateTime: "2025-06-01T19:00:00-04:00",
    timeZone: TimeZone.AMERICA_NEW_YORK,
    location: {
      coordinates: {
        latitude: 39.947716, // Approximate latitude for 6th & Walnut Streets
        longitude: -75.151085, // Approximate longitude for 6th & Walnut Streets
      },
      address: {
        country: "United States",
        countryCode: "US",
        street: "Near 6th & Walnut Streets (March Start Point)",
        city: "Philadelphia",
        state: "Pennsylvania",
        stateCode: "PA",
        postalCode: "19106",
        county: "Philadelphia County",
      },
    },
    // festivalLocation: {
    //     "description": "Festival held in the Gayborhood, spanning Walnut Street to Pine Street, and Quince Street to Juniper Street.",
    //     "coordinates": {
    //         "latitude": 39.947800,
    //         "longitude": -75.162080
    //     },
    //     "address": {
    //         "country": "United States",
    //         "countryCode": "US",
    //         "street": "1234 Locust Street (central Gayborhood area)",
    //         "city": "Philadelphia",
    //         "state": "Pennsylvania",
    //         "stateCode": "PA",
    //         "postalCode": "19107",
    //         "county": "Philadelphia County"
    //     }
    // },
    schedule: [
      {
        startDateTime: "10:30",
        endDateTime: "12:00",
        title: "The Philly Pride March",
        description:
          "Community march from 6th & Walnut Streets, proceeding through Center City and ending in the Gayborhood. Features the 600-foot Progress Pride flag.",
      },
      {
        startDateTime: "12:00",
        endDateTime: "19:00",
        title: "Philly Pride Festival",
        description:
          "Expansive street festival in the Gayborhood (Walnut St to Pine St, Quince St to Juniper St) with live entertainment, food trucks, over 200 vendors, and community resources.",
      },
    ],
    highlights: [
      {
        rank: 1,
        title: "Theme: Philly L.U.V.",
        description:
          "Celebrating Legacy, Unity, and Visibility throughout the event.",
      },
      {
        rank: 2,
        title: "Record 600-Foot Progress Pride Flag",
        description:
          "Witness the largest Pride flag in Philadelphia’s history during the march.",
      },
      {
        rank: 3,
        title: "Historic March Route",
        description:
          "The march starts near historic Independence Hall, an area significant to early LGBTQ+ rights demonstrations.",
      },
      {
        rank: 4,
        title: "Expanded Gayborhood Festival & LUV ERS Zones",
        description:
          "Six themed entertainment zones: Legacy, Unity, Visibility, Everyone, Relaxed, and Sapphic, plus over 200 vendors and food trucks.",
      },
      {
        rank: 5,
        title: "Community & Specialty Spaces",
        description:
          "Includes a Youth & Family Zone, Sober Vibes Space (alcohol-free), Kiki Alley (celebrating ballroom culture), and an indoor Sensory-Friendly Zone.",
      },
      {
        rank: 6,
        title: "50th Anniversary Commemoration",
        description:
          "Honoring 50 years since Pennsylvania's first executive order protecting LGBTQ+ state employees from discrimination.",
      },
    ],
    gallery: {
      id: "philly-pride",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/philadelphia/gallery/photo1.jpg"),
          alt: "An elevated view captures a large, elongated Pride flag, featuring rainbow, trans, and brown/black stripes, being carried down a city street lined with brick buildings and trees. Many people are walking alongside the flag, and some cars are parked on the street.",
          tags: ["people", "parade"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/philadelphia/gallery/photo2.jpg"),
          alt: "A wide shot shows a large crowd of people holding a massive Pride flag with rainbow, trans, and brown/black stripes in front of a grand, classical building with columns, likely City Hall. Modern skyscrapers rise in the background under a partly cloudy sky.",
          tags: ["people", "parade"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/philadelphia/gallery/photo3.jpg"),
          alt: "From behind, two individuals walk forward on a street, part of a larger group carrying a vast Pride flag. The flag, with its rainbow colors transitioning into pink, white, and light blue, and then brown and black, stretches across the width of the frame above their heads.",
          tags: ["people", "parade"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/philadelphia/gallery/photo4.jpg"),
          alt: `A dense crowd of people fills a city street during what appears to be a Pride celebration. A banner hangs across the street in the background that reads "HAPPY PRIDE ON CAMAC" with rainbow flags visible on buildings. Many attendees are wearing colorful clothing or accessories.`,
          tags: ["people", "parade"],
        },
        {
          id: "5",
          uri: require("@/assets/images/screens/events/philadelphia/gallery/photo5.jpg"),
          alt: "A street scene shows a crowd of people gathered outside brick buildings, some adorned with rainbow flags. The sky is a soft twilight blue, and streetlights are beginning to glow.",
          tags: ["people", "parade"],
        },
      ],
      tags: [
        { id: "people", label: "people", color: "#FF0000" },
        { id: "map", label: "map", color: "#00FF00" },
        { id: "swag", label: "swag", color: "#FFFF00" },
        { id: "flag", label: "flag", color: "#FFFF00" },
      ],
    },
    expectedAttendance: 100000, // Based on previous year, likely to be higher
    tags: [EventTag.OFFICIAL, EventTag.FEATURE],
    website: "https://phillypride365.org/philly-pride-2025",
    links: [
      {
        type: "website",
        url: "https://phillypride365.org/philly-pride-2025",
      },
      {
        type: "facebook",
        url: "https://www.facebook.com/phillypride365/",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/Phillypride365/",
      },
    ],
    transportations: [
      {
        mode: "train",
        title: "SEPTA Subway & Regional Rail",
        duration: "Short walk from stations",
        icon: "train",
        instructions: [
          "For March Start (6th & Walnut): Take SEPTA Market-Frankford Line (L) to 5th St/Independence Hall station.",
          "For Festival (Gayborhood): Take SEPTA Broad Street Line (B) to Walnut-Locust station. Regional Rail lines to Jefferson Station are also convenient. Market-Frankford Line (L) or Trolley Lines (T) to 13th St station.",
        ],
      },
      {
        mode: "bus",
        title: "SEPTA Bus",
        duration: "Varies, stops may be affected by closures",
        icon: "directions-bus",
        instructions: [
          "Numerous bus routes serve Center City.",
          "Check SEPTA's website (septa.org) for specific route information, detours, and weekend service changes closer to the event date.",
        ],
      },
      {
        mode: "rideshare",
        title: "Uber/Lyft",
        duration: "Varies with traffic and closures",
        icon: "local-taxi",
        instructions: [
          "Set your destination to a location a few blocks away from the march route or festival footprint due to extensive street closures.",
          "Expect surge pricing and significant delays in the Center City area.",
          "Public transport is strongly recommended over ride-sharing or driving.",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "Varies based on your location",
        icon: "directions-walk",
        instructions: [
          "If you are in Center City or surrounding neighborhoods, walking may be a viable option.",
          "Prepare for large pedestrian crowds and wear comfortable shoes.",
        ],
      },
    ],
  },
  {
    id: "13",
    title: "WorldPride DC Parade",
    description: `The iconic Capital Pride Parade transforms into the WorldPride Parade for WorldPride Washington, DC 2025. Taking place on Saturday, June 7, this beloved tradition honors the history and evolution of LGBTQ+ neighborhoods in Washington, DC, and the ongoing fight for equality. 2025 also marks the 50th Anniversary of Pride celebrations in the nation's capital.

The parade will feature ornate floats, grand marshals, and a vast array of community groups marching down iconic DC streets, including 14th Street and Pennsylvania Avenue, with the U.S. Capitol as a stunning backdrop. It's a key event in the larger WorldPride DC celebrations, which are expected to draw millions.

The celebration continues with the two-day WorldPride Street Festival and Concert along Pennsylvania Avenue, starting concurrently with the parade on Saturday and continuing through Sunday.`,
    type: EventCategory.PARADE,
    status: EventStatus.CONFIRMED,
    coverImageUri: require("@/assets/images/screens/events/dc/cover.jpg"),
    images: [],
    startDateTime: "2025-06-07T13:00:00-04:00", // 1:00 PM EDT
    endDateTime: "2025-06-07T20:00:00-04:00", // 8:00 PM EDT
    timeZone: TimeZone.AMERICA_NEW_YORK,
    location: {
      // Parade Start Location
      coordinates: {
        latitude: 38.9169, // Approximate for 14th & T St NW, precise search yielded general area
        longitude: -77.032, // Approximate for 14th & T St NW
      },
      address: {
        country: "United States",
        countryCode: "US",
        street: "14th Street NW & T Street NW",
        city: "Washington",
        state: "District of Columbia",
        stateCode: "DC",
        postalCode: "20009",
        county: "District of Columbia",
      },
    },
    // // The parade ends near Pennsylvania Ave & 9th St NW, leading into the festival.
    // // The festival location is distinct and broader.
    // festivalLocation: {
    //     description: "WorldPride Street Festival and Concert takes place on Pennsylvania Avenue NW between 3rd and 9th Streets, Constitution Ave NW between 3rd & 7th, and 3rd St NW between Constitution & Independence.",
    //     coordinates: {
    //         // Central point for festival area (e.g., Penn Ave NW & 6th St NW)
    //         latitude: 38.895_approx,  // Approximate for Penn Ave between 3rd and 9th
    //         longitude: -77.020_approx // Approximate for Penn Ave between 3rd and 9th
    //         // More precise coordinates for "Pennsylvania Ave NW & 6th St NW, Washington, DC": 38.8948° N, 77.0198° W (from a quick map check)
    //         // latitude: 38.8948,
    //         // longitude: -77.0198
    //     },
    //     address: {
    //         country: "United States",
    //         countryCode: "US",
    //         street: "Pennsylvania Avenue NW (between 3rd & 9th Streets)",
    //         city: "Washington",
    //         state: "District of Columbia",
    //         stateCode: "DC",
    //         postalCode: "20004", // Covers this part of Pennsylvania Ave
    //         county: "District of Columbia"
    //     }
    // },
    schedule: [
      {
        startDateTime: "Sat, June 7, 13:00", // 1:00 PM EDT
        endDateTime: "20:00", // 8:00 PM EDT
        title: "WorldPride DC Parade",
        description:
          "The parade begins at 14th & T Streets NW, proceeds south on 14th St, then east on Pennsylvania Ave, ending near 9th St NW / Constitution Ave NW.",
      },
      {
        startDateTime: "Sat, June 7, 12:00", // June 7th, 12:00 PM EDT
        endDateTime: "22:00", // Approx. end time for Day 1 festival activities, check official for exact
        title: "WorldPride Street Festival - Day 1",
        description:
          "Free street festival on Pennsylvania Ave NW (3rd-9th Sts). Features exhibitors, food, beverage gardens, and multiple entertainment stages. Cynthia Erivo performs in the evening.",
      },
      {
        startDateTime: "Sun, June 8, 12:00", // June 8th, 12:00 PM EDT
        endDateTime: "22:00", // June 8th, 10:00 PM EDT
        title: "WorldPride Street Festival - Day 2",
        description:
          "Continues with hundreds of exhibitors, food, entertainment. Evening concert featuring Doechii, Khalid, Brooke Eden, and 2AM Ricky.",
      },
    ],
    highlights: [
      {
        rank: 1,
        title: "WorldPride Designation",
        description:
          "Washington, DC hosts the prestigious global WorldPride celebration in 2025.",
      },
      {
        rank: 2,
        title: "50th Anniversary of DC Pride",
        description:
          "Commemorating half a century of Pride celebrations in the nation's capital.",
      },
      {
        rank: 3,
        title: "Iconic Parade Route",
        description:
          "Marching down historic 14th Street and Pennsylvania Avenue, with the U.S. Capitol as a backdrop.",
      },
      {
        rank: 4,
        title: "Two-Day Street Festival & Concerts",
        description:
          "A massive, free festival on Pennsylvania Avenue with hundreds of vendors, diverse food, and multiple stages featuring international headliners and local talent.",
      },
      {
        rank: 5,
        title: "Camp Pride Family Area",
        description:
          "A dedicated fun-filled oasis for kids and families within the WorldPride celebrations.",
      },
    ],
    gallery: {
      id: "dc-worldpride",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/dc/gallery/photo1.jpg"),
          alt: `A vibrant Pride parade scene shows a diverse group of people marching down a street. Many are carrying large clusters of rainbow-colored balloons, and one person in the foreground holds a white banner that reads "#EqualityforAll" with a rainbow heart.`,
          tags: ["people", "parade"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/dc/gallery/photo2.jpg"),
          alt: `A person wearing a white cowboy hat and sunglasses, a black t-shirt, and a necklace waves a large rainbow Pride flag. They are standing in front of a bright pink parade float that has "HOT 99.5" written on it.`,
          tags: ["people", "parade"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/dc/gallery/photo3.jpg"),
          alt: `Two individuals smile as confetti rains down on a city street during what appears to be a lively Pride celebration. One person in a "Be You" t-shirt smiles towards the viewer, while another to the right, wearing a red top, aims a confetti cannon. Other celebrants are visible in the background, along with city buildings.`,
          tags: ["people", "parade"],
        },
        {
          id: "4",
          uri: require("@/assets/images/screens/events/dc/gallery/photo4.jpg"),
          alt: `A graphic map outlines the "WorldPride Washington DC 2025 Parade" route for Saturday, June 7, 2025. The route, shown in purple, is 1.8 miles long, with numbered points indicating the start, accessibility zones, announcement stands, a block party, review stand, family area, viewing area, grandstand bleachers, and the end location with a tea dance party. Metro stations are also marked along the route.`,
          tags: ["map"],
        },
      ],
      tags: [
        { id: "people", label: "people", color: "#FF0000" },
        { id: "parade", label: "parade", color: "#FF00FF" },
        { id: "map", label: "map", color: "#00FF00" },
      ],
    },
    expectedAttendance: 1000000, // total for the worldpride event
    tags: [EventTag.OFFICIAL, EventTag.FEATURE],
    website: "https://worldpridedc.org/",
    links: [
      {
        type: "website",
        url: "https://worldpridedc.org/",
      },
      // {
      //   type: "parade_info",
      //   url: "https://worldpridedc.org/events/parade/",
      // },
      // {
      //   type: "festival_info",
      //   url: "https://worldpridedc.org/events/street-festival/",
      // },
      {
        type: "facebook",
        url: "https://www.facebook.com/CapitalPrideDC",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/capitalpridedc/",
      },
      {
        type: "youtube",
        url: "https://www.youtube.com/@dccapitalpride/",
      },
    ],
    transportations: [
      {
        mode: "train", // Metro
        title: "Washington Metro (WMATA)",
        duration: "Varies, short walk from multiple stations",
        icon: "train",
        instructions: [
          "Public transportation is highly recommended due to street closures and large crowds.",
          "For Parade Start (14th & T St NW area/Logan Circle): U Street/African-Amer Civil War Memorial/Cardozo Station (Green Lines) or Dupont Circle (Red Line) then walk.",
          "For Parade Route & Festival (Pennsylvania Ave): Federal Triangle (Blue/Orange/Silver Lines), Archives-Navy Memorial-Penn Quarter (Green/Yellow Lines), Metro Center (Red/Blue/Orange/Silver Lines).",
          "Check WMATA's website (wmata.com) for service updates, potential delays, and station information closer to the event.",
        ],
      },
      {
        mode: "bus",
        title: "Metrobus (WMATA)",
        duration: "Varies, routes will be heavily impacted by detours",
        icon: "directions-bus",
        instructions: [
          "Many bus routes operate in the area, but expect significant detours due to street closures for the parade and festival.",
          "Consult WMATA's website for specific bus route changes and advisories.",
        ],
      },
      {
        mode: "rideshare",
        title: "Car / Ride-share",
        duration: "Not recommended for direct access",
        icon: "local-taxi",
        instructions: [
          "Set your destination to a location a few blocks away from the march route or festival footprint due to extensive street closures.",
          "Expect surge pricing and significant delays in the Center City area.",
          "Public transport is strongly recommended over ride-sharing or driving.",
        ],
      },
      {
        mode: "walk",
        title: "Walking",
        duration: "Varies based on your location",
        icon: "directions-walk",
        instructions: [
          "If staying nearby, walking may be feasible but be prepared for very large crowds.",
          "Wear comfortable shoes and stay hydrated.",
        ],
      },
    ],
  },
  {
    id: "14",
    title: "55th Annual LA Pride Parade",
    description: `The 55th Annual LA Pride Parade, with the theme "Pride Marches On," celebrates the LGBTQ+ community's tenacity, resilience, and the rich history of Pride in Los Angeles. Organized by the Christopher Street West Association (CSW), this iconic event is the oldest, largest, and longest-running Pride Parade in Southern California.

The parade will feature over 120 contingents, including vibrant floats, inspiring Grand Marshals (Niecy Nash & Jessica Betts, Andrew Rannells, and TrinoxAdam for 2025), and diverse community groups marching down historic Hollywood Boulevard. The event will be broadcast live on ABC7 and streamed on Hulu.

Immediately following and alongside the parade, the free LA Pride Village (formerly Block Party) will offer a street fair experience on Hollywood Boulevard with multiple stages, vendors, food trucks, and community programming, continuing the celebration into the early evening.`,
    type: EventCategory.PARADE,
    status: EventStatus.CONFIRMED,
    coverImageUri: require("@/assets/images/screens/events/la/cover.jpg"),
    images: [],
    startDateTime: "2025-06-08T11:00:00-07:00", // 11:00 AM PDT
    endDateTime: "2025-06-08T15:00:00-07:00", // Estimated parade end time ~3:00 PM PDT
    timeZone: TimeZone.AMERICA_LOS_ANGELES,
    location: {
      // Parade Start Location
      coordinates: {
        latitude: 34.098, // Approx. for Sunset Blvd & Highland Ave
        longitude: -118.3387, // Approx. for Sunset Blvd & Highland Ave
      },
      address: {
        country: "United States",
        countryCode: "US",
        street: "Sunset Blvd & N Highland Ave",
        city: "Los Angeles",
        state: "California",
        stateCode: "CA",
        postalCode: "90028", // Common for this area of Hollywood
        county: "Los Angeles County",
      },
    },
    // // Parade End Location: Sunset Blvd & Cahuenga Blvd
    // // LA Pride Village is the main festival area.
    // festivalLocation: {
    //   description:
    //     "LA Pride Village, a free street festival on Hollywood Boulevard between Argyle Ave and mid-Bronson Ave.",
    //   coordinates: {
    //     // Central point for Pride Village (e.g., Hollywood Blvd & Argyle Ave)
    //     latitude: 34.1016, // Approx. for Hollywood Blvd & Argyle Ave
    //     longitude: -118.3267, // Approx. for Hollywood Blvd & Argyle Ave
    //   },
    //   address: {
    //     country: "United States",
    //     countryCode: "US",
    //     street: "Hollywood Boulevard (between Argyle Ave & Bronson Ave)",
    //     city: "Los Angeles",
    //     state: "California",
    //     stateCode: "CA",
    //     postalCode: "90028", // Common for this area of Hollywood
    //     county: "Los Angeles County",
    //   },
    // },
    schedule: [
      {
        startDateTime: "11:00", // 11:00 AM PDT
        endDateTime: "13:00", // Approx. 3:00 PM PDT (Parade duration can vary)
        title: "55th Annual LA Pride Parade",
        description:
          "Steps off at Sunset Blvd & Highland Ave, proceeds north on Highland to Hollywood Blvd, east on Hollywood Blvd to Cahuenga Blvd, then south on Cahuenga to end at Sunset Blvd.",
      },
      {
        startDateTime: "11:00", // https://lapride.org/event/2025-la-pride-village/
        endDateTime: "20:00",
        title: "LA Pride Village",
        description:
          "Free street festival on Hollywood Blvd (Argyle to mid-Bronson). Features programming on two stages, over 100 vendors, exhibitors, sponsors and food choices",
      },
    ],
    highlights: [
      {
        rank: 1,
        title: "Theme: Pride Marches On",
        description:
          "Celebrating the LGBTQ+ community's tenacity, resilience, and continued progress.",
      },
      {
        rank: 2,
        title: "Star-Studded Grand Marshals",
        description:
          "Honoring Vanguard: Niecy Nash & Jessica Betts, Celebrity: Andrew Rannells, and Community: TrinoxAdam.",
      },
      {
        rank: 3,
        title: "Iconic Hollywood Route",
        description:
          "The parade takes over historic Hollywood Boulevard, a return to its roots.",
      },
      {
        rank: 4,
        title: "Free LA Pride Village",
        description:
          "A large, free street festival adjacent to the parade with multiple stages, vendors, food, art, and community engagement.",
      },
      {
        rank: 5,
        title: "Live Broadcast & Streaming",
        description:
          "Extensive live coverage on ABC7 and Hulu, making the celebration accessible to a wider audience.",
      },
      {
        rank: 6,
        title: "Community Focused",
        description:
          "Emphasis on being free and accessible to all, with a special 'Heroes of the Wildfires' tribute section in the parade.",
      },
    ],
    gallery: {
      id: "la-pride-2025",
      title: "",
      images: [
        {
          id: "1",
          uri: require("@/assets/images/screens/events/la/gallery/photo1.jpg"),
          alt: "A vibrant scene from a Pride parade features a large bisexual flag being carried among a crowd of people. Many colorful balloons are visible, adding to the festive atmosphere.",
          tags: ["people", "parade"],
        },
        {
          id: "2",
          uri: require("@/assets/images/screens/events/la/gallery/photo2.jpg"),
          alt: "A group of people are shown in a parade, one of whom is holding a large, flowing rainbow fan. The backdrop includes a building with a pink facade and several windows.",
          tags: ["people", "parade"],
        },
        {
          id: "3",
          uri: require("@/assets/images/screens/events/la/gallery/photo3.jpg"),
          alt: `A person in a colorful, layered rainbow dress poses in front of a parade float adorned with flowers and a large heart. The float also displays the text "LA County Loves You."`,
          tags: ["people", "parade"],
        },
      ],
      tags: [
        { id: "people", label: "people", color: "#FF0000" },
        { id: "parade", label: "parade", color: "#FF00FF" },
        { id: "festival", label: "festival", color: "#00FF00" },
        { id: "map", label: "map", color: "#00FF00" },
      ],
    },
    expectedAttendance: 140000, // For the parade, based on "similar to last year" figures. Pride Village ~44,000.
    tags: [EventTag.OFFICIAL, EventTag.FEATURE],
    website: "https://lapride.org/",
    links: [
      {
        type: "website",
        url: "https://lapride.org/",
      },
      // {
      //   type: "parade_info",
      //   url: "https://lapride.org/event/2025-la-pride-parade/",
      // },
      {
        type: "tiktok",
        url: "https://www.tiktok.com/@lapride",
      },
      {
        type: "facebook",
        url: "https://www.facebook.com/LAPride",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/lapride/",
      },
      {
        type: "youtube",
        url: "https://www.youtube.com/c/LAPride", // Found https://www.youtube.com/nycpride2
      },
    ],
    transportations: [
      {
        mode: "train", // Metro Rail
        title: "LA Metro Rail (B/D Lines)",
        duration: "Short walk from stations",
        icon: "train",
        instructions: [
          "Public transportation is highly recommended.",
          "Take the Metro B Line (formerly Red Line) or D Line (formerly Purple Line).",
          "For the Parade: Use Hollywood/Highland Station (will be renamed 'LA PRIDE NATION' for the event).",
          "For LA Pride Village: Use Hollywood/Vine Station (will be renamed 'HOLLYWOOD & PRIDE' for the event).",
          "Save time and avoid long lines when you pay with your phone. Before heading out to the event, add TAP Transit Card to your iPhone Apple Wallet or download the TAP app, then add $3.50 roundtrip fare. Tap at the fare gates and board!",
          "Check Metro's website (metro.net) for service updates, station renaming details, and schedules.",
        ],
      },
      {
        mode: "rideshare",
        title: "Car / Ride-share",
        duration: "Not recommended for direct access",
        icon: "local-taxi",
        instructions: [
          "Expect surge pricing and extreme difficulty accessing areas near the parade route and festival footprint.",
          "If using, select drop-off/pick-up points several blocks away from the event areas and walk.",
        ],
      },
    ],
  },
];

export function getEventById(eventId: string): EventOverview {
  const event = PRIDE_EVENTS_2025.find((event) => event.id === eventId);
  if (!event) {
    throw new Error(`Event with ID "${eventId}" not found.`);
  }
  return event;
}
